import { Injectable } from '@angular/core';
import moment from 'moment';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { orderBy } from 'lodash-es';
import { CacheService, IEntityCacheService } from '@nimbus-library/features/cache/index';
import { SearchParserResult } from '@nimbus-library/features/graph/models/search-parser-result.model';
import { GraphUserSearchApiClient } from '@nimbus-library/features/graph/services/graph-user-search.api-client';
import { UserSearchMapper } from '@nimbus-library/features/shared/services/user-search/mappers/user-search.mapper';
import { ContactSearchMapper } from '@nimbus-library/features/shared/services/user-search/mappers/contact-search.mapper';
import { IContact } from '@nimbus-library/features/shared/models/contacts/i-contact.model';
import { ContactsODataService } from '@nimbus-library/features/shared/services/contacts-odata/api/contacts-odata.service';
import { TeamManagerApiClient } from '@nimbus-global/features/shared/services/api/team-manager.api-client';
import { IServiceLine } from '@nimbus-global/features/shared/models/i-service-line';
import { SearchOptions } from '@nimbus-library/features/graph/enums/search-options.enum';
import { GraphApiClient } from '@nimbus-library/features/graph/services/graph.api-client';
import { TenantDirectoryApiClient } from '@nimbus-global/features/shared/services/api/tenant-directory.api-client';
import { IUserAutocompleteOption } from '../../components/material/input-user-autocomplete/i-user-auto-complete-option.model';

@Injectable({ providedIn: 'root' })
export class AutoCompleteUserSearchService {
  private readonly CACHE_TIMEOUT = moment.duration(5, 'minutes');
  private readonly PRELOAD_TIMEOUT = moment.duration(5, 'minutes');
  private readonly entityCache: IEntityCacheService<IServiceLine[]>;

  private readonly graphSearchFields = [
    'id',
    'displayName',
    'userPrincipalName',
    'mail',
    'emailAddresses',
    'businessPhones',
    'mobilePhone',
  ];
  public constructor(
    private readonly userSearchApiClient: GraphUserSearchApiClient,
    private readonly graphApiClient: GraphApiClient,
    cacheService: CacheService,
    private readonly contactODataService: ContactsODataService,
    private readonly teamManagerApiClient: TeamManagerApiClient,
    private readonly tenantDirectoryApiClient: TenantDirectoryApiClient,
  ) {
    this.entityCache = cacheService.for<IServiceLine[]>('IServiceLines', {
      cacheMaxAge: this.CACHE_TIMEOUT,
      preloadMaxAge: this.PRELOAD_TIMEOUT,
    });
  }

  /**
   * `directoryOrganizationUnitId` switches the user search from the signed-in user's delegated Graph to a server-side
   * search of `tenantId`'s directory, authorized on that organization unit (admin editing a user of another tenant).
   */
  public search(
    input: string,
    tenantId: string,
    pageSize: number,
    directoryOrganizationUnitId: string | null = null,
  ): Observable<IUserAutocompleteOption[]> {
    const searchInput = new SearchParserResult([SearchOptions.AccountEnabled]);
    searchInput.plainSearchText = input;
    searchInput.originalInput = input;

    const cacheServices = this.entityCache.getInfo(tenantId);
    const serviceUpn$ =
      !!cacheServices && cacheServices.validForCache
        ? of(cacheServices.value)
        : this.teamManagerApiClient.getServiceLinesByTenant(tenantId).pipe(this.entityCache.useCache(tenantId, false));

    return serviceUpn$.pipe(
      switchMap((services) =>
        combineLatest([
          directoryOrganizationUnitId
            ? this.getTenantDirectorySearch(input, tenantId, directoryOrganizationUnitId, pageSize)
            : this.getGraphUserSearch(searchInput, tenantId, pageSize),
          this.getContactSearch(searchInput, tenantId, pageSize),
        ]).pipe(
          map((data) => [...data[0], ...data[1]]),
          map((data) => data.filter((d) => !(services || []).find((i) => d.callOptions.find((option) => option.text === i.upn)))),
          map((data: IUserAutocompleteOption[]) => orderBy(data, (d) => d.name.toLowerCase())),
        ),
      ),
    );
  }

  private getGraphUserSearch(searchInput: SearchParserResult, tenantId: string, pageSize: number): Observable<IUserAutocompleteOption[]> {
    return this.graphApiClient.hasExtendedPermission().pipe(
      switchMap((hasPermissions: boolean) => {
        if (hasPermissions) {
          searchInput.searchExpression[SearchOptions.AccountEnabled] = 'true';
        }

        return this.userSearchApiClient
          .getContacts<IUserAutocompleteOption>(
            (u) => new UserSearchMapper().handle(u, tenantId),
            this.graphSearchFields,
            searchInput,
            null,
            pageSize,
          )
          .pipe(switchMap((data) => data.getNext().pipe(switchMap(() => data.data$.pipe(take(1))))));
      }),
    );
  }

  private getTenantDirectorySearch(
    input: string,
    tenantId: string,
    organizationUnitId: string,
    pageSize: number,
  ): Observable<IUserAutocompleteOption[]> {
    const mapper = new UserSearchMapper();
    return this.tenantDirectoryApiClient
      .searchUsersForDirectCall(tenantId, organizationUnitId, input, pageSize)
      .pipe(
        map((users) =>
          users.map((user) => mapper.handle({ id: user.o365Id, displayName: user.displayName, userPrincipalName: user.upn }, tenantId)),
        ),
      );
  }

  private getContactSearch(searchInput: SearchParserResult, tenantId: string, pageSize: number): Observable<IUserAutocompleteOption[]> {
    return of(this.contactODataService.getContactsInfoByQuery(searchInput, pageSize, tenantId)).pipe(
      switchMap((data) =>
        data.getNext().pipe(
          switchMap(() =>
            data.data$.pipe(
              take(1),
              map((contacts: IContact[]) => contacts.map((contact) => new ContactSearchMapper().handle(contact))),
            ),
          ),
        ),
      ),
    );
  }
}
